// ═══════════════════════════════════════════════════════════════════════════
// Runner v3 Types & Schemas
// ═══════════════════════════════════════════════════════════════════════════

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────
// ENUMS & TYPES
// ─────────────────────────────────────────────────────────────────────────

export type RunnerOS = 'linux' | 'windows' | 'macos';
export type RunnerArch = 'x64' | 'arm64';
export type RunnerV3Mode = 'headless' | 'ui';
export type RunnerV3Status = 'online' | 'offline' | 'error';
export type RunnerEventType =
  | 'registered'
  | 'heartbeat'
  | 'job_started'
  | 'job_progress'
  | 'job_finished'
  | 'error';

// ─────────────────────────────────────────────────────────────────────────
// CAPABILITIES
// ─────────────────────────────────────────────────────────────────────────

export interface RunnerV3Capabilities {
  files: boolean;              // Manipular arquivos (read/write)
  shell: boolean;              // Executar shell commands
  ui: boolean;                 // Controlar tela (mouse/keyboard)
  screen_stream: boolean;      // Capturar tela (stream)
  docker: boolean;             // Controlar Docker
  git: boolean;                // Operações Git
  gpu_cuda: boolean;           // NVIDIA GPU support
  network_tools: boolean;      // curl, wget, ping, etc
  language_runtimes?: {
    node?: boolean;
    python?: boolean;
    ruby?: boolean;
    go?: boolean;
  };
}

export const CapabilitiesSchema = z.object({
  files: z.boolean().optional(),
  shell: z.boolean().optional(),
  ui: z.boolean().optional(),
  screen_stream: z.boolean().optional(),
  docker: z.boolean().optional(),
  git: z.boolean().optional(),
  gpu_cuda: z.boolean().optional(),
  network_tools: z.boolean().optional(),
  language_runtimes: z
    .object({
      node: z.boolean().optional(),
      python: z.boolean().optional(),
      ruby: z.boolean().optional(),
      go: z.boolean().optional(),
    })
    .optional(),
});

// ─────────────────────────────────────────────────────────────────────────
// RUNNER v3 MAIN TABLE
// ─────────────────────────────────────────────────────────────────────────

export interface RunnerV3 {
  id: string;                  // UUID
  customer_id: string;
  name: string;                // Ex: "Linux Docker 1", "My Windows Runner"
  os: RunnerOS;
  arch: RunnerArch;
  mode: RunnerV3Mode;
  version: string;             // Ex: "3.0.0"
  capabilities_json: string;   // JSON string (capabilities)
  token_hash: string;          // SHA-256 hash of token
  status: RunnerV3Status;
  notes?: string;
  created_at: string;          // ISO 8601
  last_seen_at?: string;       // ISO 8601, last heartbeat
}

export const RunnerV3Schema = z.object({
  id: z.string().optional(),
  customer_id: z.string(),
  name: z.string().min(1).max(255),
  os: z.enum(['linux', 'windows', 'macos']).optional(),
  arch: z.enum(['x64', 'arm64']).optional(),
  mode: z.enum(['headless', 'ui']).optional(),
  version: z.string().optional(),
  capabilities_json: z.string(),
  token_hash: z.string().optional(),
  status: z.enum(['online', 'offline', 'error']).optional(),
  notes: z.string().optional(),
  created_at: z.string().optional(),
  last_seen_at: z.string().datetime().optional(),
});

// Convenience method to parse capabilities
export function parseCapabilities(json: string): RunnerV3Capabilities {
  try {
    return JSON.parse(json) as RunnerV3Capabilities;
  } catch {
    return {
      files: false,
      shell: false,
      ui: false,
      screen_stream: false,
      docker: false,
      git: false,
      gpu_cuda: false,
      network_tools: false,
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// PAIRING TOKEN
// ─────────────────────────────────────────────────────────────────────────

export interface PairingTokenPayload {
  os: RunnerOS;
  mode: RunnerV3Mode;
  name: string;
  installation_type?: 'docker' | 'binary' | 'service' | 'app' | 'agent';
  arch?: RunnerArch;
}

export interface RunnerPairingToken {
  id: string;                  // UUID
  customer_id: string;
  token_hash: string;          // SHA-256 hash
  expires_at: string;          // ISO 8601
  used_at?: string;            // ISO 8601
  used_by_runner_id?: string;  // FK to runners_v3
  payload_json: string;        // JSON string (PairingTokenPayload)
  created_at: string;          // ISO 8601
}

export const RunnerPairingTokenSchema = z.object({
  id: z.string().optional(),
  customer_id: z.string(),
  token_hash: z.string().optional(),
  expires_at: z.string().optional(),
  used_at: z.string().datetime().optional(),
  used_by_runner_id: z.string().optional(),
  payload_json: z.string(),
  created_at: z.string().optional(),
});

export const PairingTokenPayloadSchema = z.object({
  os: z.enum(['linux', 'windows', 'macos']).optional(),
  mode: z.enum(['headless', 'ui']).optional(),
  name: z.string().min(1).max(255),
  installation_type: z
    .enum(['docker', 'binary', 'service', 'app', 'agent'])
    .optional(),
  arch: z.enum(['x64', 'arm64']).optional(),
});

// ─────────────────────────────────────────────────────────────────────────
// RUNNER EVENT
// ─────────────────────────────────────────────────────────────────────────

export type RunnerEventPayload = Record<string, any>;

export interface RunnerV3Event {
  id: string;                  // UUID
  runner_id: string;           // FK to runners_v3
  type: RunnerEventType;
  payload_json?: string;       // JSON string
  created_at: string;          // ISO 8601
}

export const RunnerV3EventSchema = z.object({
  id: z.string().optional(),
  runner_id: z.string().optional(),
  type: z.enum([
    'registered',
    'heartbeat',
    'job_started',
    'job_progress',
    'job_finished',
    'error',
  ]).optional(),
  payload_json: z.string().optional(),
  created_at: z.string().optional(),
});

// ─────────────────────────────────────────────────────────────────────────
// REQUEST/RESPONSE TYPES
// ─────────────────────────────────────────────────────────────────────────

// Generate pairing token (called by frontend)
export interface GeneratePairingTokenRequest {
  os: RunnerOS;
  mode: RunnerV3Mode;
  name: string;
  arch?: RunnerArch;
  installation_type?: 'docker' | 'binary' | 'service' | 'app' | 'agent';
}

export interface GeneratePairingTokenResponse {
  token: string;               // Raw token (only shown once!)
  expires_at: string;          // ISO 8601
}

// Validate pairing token (called by runner before registering)
export interface ValidatePairingTokenRequest {
  token_hash: string;          // Runner sends token hash
}

export interface ValidatePairingTokenResponse {
  valid: boolean;
  payload?: PairingTokenPayload;
}

// Register runner (called after validation)
export interface RegisterRunnerRequest {
  pairing_token_id: string;    // UUID of pairing token
  capabilities: RunnerV3Capabilities;
  version: string;
  arch: RunnerArch;
}

export interface RegisterRunnerResponse {
  runner: RunnerV3;
}

// Heartbeat (called every 30s)
export interface RunnerHeartbeatPayload {
  status: RunnerV3Status;
  cpu_percent?: number;
  memory_percent?: number;
  disk_free_gb?: number;
  uptime_sec?: number;
  capabilities?: RunnerV3Capabilities;
  last_execution_at?: string;
}

export interface RunnerHeartbeatRequest {
  runner_id: string;
  payload: RunnerHeartbeatPayload;
}

// List runners by customer
export interface ListRunnersV3Request {
  customer_id: string;
  os?: RunnerOS;
  mode?: RunnerV3Mode;
  status?: RunnerV3Status;
}

export interface ListRunnersV3Response {
  runners: RunnerV3[];
  total: number;
}

// Get runner detail
export interface GetRunnerV3Response {
  runner: RunnerV3;
  capabilities: RunnerV3Capabilities;
  last_events?: RunnerV3Event[];
}

// ─────────────────────────────────────────────────────────────────────────
// DB QUERIES (helper types)
// ─────────────────────────────────────────────────────────────────────────

export interface RunnerV3Row {
  id: string;
  customer_id: string;
  name: string;
  os: string;
  arch: string;
  mode: string;
  version: string;
  capabilities_json: string;
  token_hash: string;
  status: string;
  notes?: string;
  created_at: string;
  last_seen_at?: string;
}

export function mapRowToRunnerV3(row: RunnerV3Row): RunnerV3 {
  return {
    id: row.id,
    customer_id: row.customer_id,
    name: row.name,
    os: row.os as RunnerOS,
    arch: row.arch as RunnerArch,
    mode: row.mode as RunnerV3Mode,
    version: row.version,
    capabilities_json: row.capabilities_json,
    token_hash: row.token_hash,
    status: row.status as RunnerV3Status,
    notes: row.notes,
    created_at: row.created_at,
    last_seen_at: row.last_seen_at,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────

/**
 * Hash a token using SHA-256 (never store raw tokens)
 * In actual implementation, use crypto.createHash('sha256')
 */
export function hashToken(token: string): string {
  // This is a placeholder - actual implementation should use crypto module
  // import { createHash } from 'crypto';
  // return createHash('sha256').update(token).digest('hex');
  return 'sha256:' + token; // TODO: implement proper hashing
}

/**
 * Get default capabilities by OS and mode
 */
export function getDefaultCapabilities(os: RunnerOS, mode: RunnerV3Mode): RunnerV3Capabilities {
  const baseCapabilities: RunnerV3Capabilities = {
    files: true,
    shell: true,
    ui: mode === 'ui',
    screen_stream: mode === 'ui',
    docker: os !== 'windows', // Docker on Windows is WSL
    git: true,
    gpu_cuda: false,
    network_tools: true,
  };

  return baseCapabilities;
}
